import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import {
  SENSITIVE_DATA_CATEGORIES,
  SENSITIVE_DATA_CATEGORY_LIST,
  extractCategoriesFromBusinessScoping,
  calculateCategoryBreachCosts
} from '../../../shared/utils/sensitiveDataCategories'

/**
 * Sensitive data category management for Impact Calibration step.
 * Auto-populates categories from business scoping, allows admin to enter
 * per-category record volumes, and displays cost breakdown.
 *
 * @param {Object} props
 * @param {string} props.clientId - Client ID
 * @param {string} props.assessmentId - Current assessment ID
 * @param {Object} props.businessScoping - assessment_responses.business_scoping
 * @param {Function} props.onVolumesChange - Callback with volumes array for loss calculations
 */
function SensitiveDataProfile({ clientId, assessmentId, businessScoping, onVolumesChange }) {
  const [volumes, setVolumes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddMenu, setShowAddMenu] = useState(false)

  // Extract auto-detected categories from business scoping
  const autoDetectedCategories = useMemo(() =>
    extractCategoriesFromBusinessScoping(businessScoping),
    [businessScoping]
  )

  // Load existing volumes from DB
  useEffect(() => {
    if (!clientId) { setLoading(false); return }

    const fetchVolumes = async () => {
      const { data, error } = await supabase
        .from('client_sensitive_data_volumes')
        .select('*')
        .eq('client_id', clientId)

      if (error) {
        console.error('[SensitiveDataProfile] Fetch error:', error)
        setLoading(false)
        return
      }

      if (data && data.length > 0) {
        setVolumes(data)
      } else {
        // Initialize from auto-detected categories
        const initial = autoDetectedCategories.map(cat => ({
          client_id: clientId,
          assessment_id: assessmentId,
          data_category: cat.key,
          data_category_label: cat.label,
          estimated_record_count: null,
          source: 'business_scoping',
          storage_locations: []
        }))
        setVolumes(initial)
      }
      setLoading(false)
    }

    fetchVolumes()
  }, [clientId, assessmentId, autoDetectedCategories])

  // Notify parent of volume changes for loss calculations
  useEffect(() => {
    if (onVolumesChange) {
      onVolumesChange(volumes)
    }
  }, [volumes, onVolumesChange])

  const formatNumberWithCommas = (value) => {
    if (value === '' || value === null || value === undefined) return ''
    const num = String(value).replace(/[^0-9]/g, '')
    if (!num) return ''
    return parseInt(num, 10).toLocaleString()
  }

  const parseFormattedNumber = (formattedValue) => {
    return formattedValue.replace(/[^0-9]/g, '')
  }

  const updateRecordCount = (categoryKey, count) => {
    const raw = typeof count === 'string' ? count.replace(/[^0-9]/g, '') : count
    setVolumes(prev => prev.map(v =>
      v.data_category === categoryKey
        ? { ...v, estimated_record_count: raw === '' ? null : parseInt(raw, 10) || null }
        : v
    ))
  }

  const updateStorageLocations = (categoryKey, locations) => {
    setVolumes(prev => prev.map(v =>
      v.data_category === categoryKey ? { ...v, storage_locations: locations } : v
    ))
  }

  const removeCategory = (categoryKey) => {
    setVolumes(prev => prev.filter(v => v.data_category !== categoryKey))
  }

  const addCategory = (categoryKey) => {
    const cat = SENSITIVE_DATA_CATEGORIES[categoryKey]
    if (!cat) return
    setVolumes(prev => [...prev, {
      client_id: clientId,
      assessment_id: assessmentId,
      data_category: cat.key,
      data_category_label: cat.label,
      estimated_record_count: null,
      source: 'manual',
      storage_locations: []
    }])
    setShowAddMenu(false)
  }

  // Save to DB
  const saveVolumes = async () => {
    if (!clientId) return

    for (const vol of volumes) {
      const { error } = await supabase
        .from('client_sensitive_data_volumes')
        .upsert({
          client_id: clientId,
          assessment_id: assessmentId,
          data_category: vol.data_category,
          data_category_label: vol.data_category_label,
          estimated_record_count: vol.estimated_record_count,
          source: vol.source,
          storage_locations: vol.storage_locations
        }, { onConflict: 'client_id,data_category' })

      if (error) {
        console.error('[SensitiveDataProfile] Save error:', error)
      }
    }

    // Remove DB records for categories that were deleted
    const activeKeys = volumes.map(v => v.data_category)
    const { data: existing } = await supabase
      .from('client_sensitive_data_volumes')
      .select('data_category')
      .eq('client_id', clientId)

    if (existing) {
      const toDelete = existing
        .filter(e => !activeKeys.includes(e.data_category))
        .map(e => e.data_category)
      if (toDelete.length > 0) {
        await supabase
          .from('client_sensitive_data_volumes')
          .delete()
          .eq('client_id', clientId)
          .in('data_category', toDelete)
      }
    }
  }

  // Expose save to parent via effect — save when component unmounts or assessment navigates
  useEffect(() => {
    return () => {
      if (volumes.length > 0 && clientId) {
        saveVolumes()
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Cost breakdown
  const costBreakdown = useMemo(() =>
    calculateCategoryBreachCosts(volumes),
    [volumes]
  )

  // Categories available to add (not already selected)
  const availableToAdd = SENSITIVE_DATA_CATEGORY_LIST.filter(
    cat => !volumes.some(v => v.data_category === cat.key)
  )

  const STORAGE_OPTIONS = ['On-premises', 'Cloud', 'Third-party/Vendor']

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-16 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Sensitive Data Profile
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Review data categories and enter estimated record volumes for breach cost modeling
          </p>
        </div>
        <button
          onClick={saveVolumes}
          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Save
        </button>
      </div>

      {volumes.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            No sensitive data categories detected. Add categories manually or complete business scoping first.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {volumes.map(vol => {
            const cat = SENSITIVE_DATA_CATEGORIES[vol.data_category]
            return (
              <div
                key={vol.data_category}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {cat?.shortLabel || vol.data_category_label}
                      </h4>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                        vol.source === 'business_scoping'
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {vol.source === 'business_scoping' ? 'Auto-detected' : 'Manual'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {cat?.description || ''}
                    </p>
                  </div>
                  <button
                    onClick={() => removeCategory(vol.data_category)}
                    className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                    title="Remove category"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Estimated Record Count
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithCommas(vol.estimated_record_count)}
                      onChange={e => updateRecordCount(vol.data_category, parseFormattedNumber(e.target.value))}
                      placeholder="e.g., 50,000"
                      className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {vol.estimated_record_count && cat && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Est. cost: ${(vol.estimated_record_count * cat.costPerRecord).toLocaleString()} at ${cat.costPerRecord}/record
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Storage Locations
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {STORAGE_OPTIONS.map(loc => (
                        <label
                          key={loc}
                          className={`text-xs px-2.5 py-1.5 rounded-lg cursor-pointer border transition-colors ${
                            (vol.storage_locations || []).includes(loc)
                              ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/30 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={(vol.storage_locations || []).includes(loc)}
                            onChange={() => {
                              const current = vol.storage_locations || []
                              const next = current.includes(loc)
                                ? current.filter(l => l !== loc)
                                : [...current, loc]
                              updateStorageLocations(vol.data_category, next)
                            }}
                            className="sr-only"
                          />
                          {loc}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add category button */}
      {availableToAdd.length > 0 && (
        <div className="mt-4 relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="text-sm font-medium text-primary-600 dark:text-accent-300 hover:underline flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Data Category
          </button>
          {showAddMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowAddMenu(false)} />
              <div className="absolute z-20 mt-1 w-72 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 max-h-48 overflow-y-auto">
                {availableToAdd.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => addCategory(cat.key)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="font-medium text-gray-900 dark:text-white">{cat.shortLabel}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">${cat.costPerRecord}/record</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Cost breakdown */}
      {costBreakdown.hasData && (
        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Estimated Data Breach Cost
          </h4>
          <div className="space-y-1.5">
            {costBreakdown.breakdown.map(item => (
              <div key={item.categoryKey} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {item.label}: {item.recordCount.toLocaleString()} records x ${item.costPerRecord}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${item.totalCost.toLocaleString()}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100 dark:border-gray-700">
              <span className="font-semibold text-gray-900 dark:text-white">Total</span>
              <span className="font-bold text-gray-900 dark:text-white">
                ${costBreakdown.totalCost.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SensitiveDataProfile
